"use client";

import React from "react";
import { Link } from "@/components/common/Link";
import { WvcLogo } from "@/integrations/wordpress/WvcLogo";
import { MenuProvider, useMenu, type MenuItem } from "@/integrations/wordpress/WordPressMenuProvider";
import { Separator } from "@/components/ui/separator";
import { ShieldCheck, Mail, Phone, MapPin, ArrowUpRight } from "lucide-react";

// Recursive menu renderer for footer navigation supporting arbitrary child depth
function RecursiveFooterLinks({
  items,
  level = 0
}: {
  items: MenuItem[];
  level?: number;
}) {
  if (!items || items.length === 0) return null;
  return <ul className={level === 0 ? "space-y-2.5" : "pl-3 mt-1.5 space-y-1.5 border-l border-border/70"}>
      {items.map((item, i) => <li key={item.id || `menu-item-${level}-${i}`} data-index={i}>
          <Link to={item.href || "#"} className="text-sm text-muted-foreground transition-colors duration-150 hover:text-foreground inline-flex items-center gap-1 group">
            <span>{item.label}</span>
            {item.children && item.children.length > 0 && <span className="text-xs text-muted-foreground/60 font-mono">({item.children.length})</span>}
          </Link>
          {item.children && item.children.length > 0 && <RecursiveFooterLinks items={item.children} level={level + 1} />}
        </li>)}
    </ul>;
}

// Consumer component calling useMenu
function FooterMenuConsumer() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="space-y-2">
        <div className="h-4 w-24 bg-muted animate-pulse rounded-sm" />
        <div className="h-4 w-32 bg-muted animate-pulse rounded-sm" />
        <div className="h-4 w-28 bg-muted animate-pulse rounded-sm" />
      </div>;
  }
  if (!menuItems || menuItems.length === 0) {
    return <ul className="space-y-2.5">
        <li data-index={0}>
          <Link to="/ResearchReportsPage" className="text-sm text-muted-foreground transition-colors duration-150 hover:text-foreground">
            Research Reports
          </Link>
        </li>
        <li data-index={1}>
          <Link to="/EbooksGuidesPage" className="text-sm text-muted-foreground transition-colors duration-150 hover:text-foreground">
            Ebooks &amp; Strategic Guides
          </Link>
        </li>
        <li data-index={2}>
          <Link to="/IndustryInsightsPage" className="text-sm text-muted-foreground transition-colors duration-150 hover:text-foreground">
            Industry Insights
          </Link>
        </li>
        <li data-index={3}>
          <Link to="/CaseStudiesPage" className="text-sm text-muted-foreground transition-colors duration-150 hover:text-foreground">
            Case Studies
          </Link>
        </li>
      </ul>;
  }
  return <RecursiveFooterLinks items={menuItems} />;
}
export default function Footer() {
  const publicationSections = [{
    label: "Executive Library",
    to: "/ResourcesPage"
  }, {
    label: "Research Reports",
    to: "/ResearchReportsPage"
  }, {
    label: "Industry Insights",
    to: "/IndustryInsightsPage"
  }, {
    label: "Case Studies",
    to: "/CaseStudiesPage"
  }, {
    label: "Briefing Newsletter",
    to: "/NewsletterPage"
  }];
  const editorialGovernance = [{
    label: "About the Publisher",
    to: "/AboutUsPage"
  }, {
    label: "Editorial Standards",
    to: "/AboutUsPage"
  }, {
    label: "Privacy & Data Protection",
    to: "/PrivacyDataProtectionPage"
  }, {
    label: "Terms & Conditions",
    to: "/TermsConditionsPage"
  }];
  return <section id="footer" data-nav="light" className="bg-background text-foreground border-t border-border relative overflow-hidden">
      {/* Masthead rule: broadsheet double hairline divider */}
      <div className="w-full border-t border-border/40 mt-1" />

      <div className="max-w-[1280px] mx-auto px-5 sm:px-8 lg:px-12 pt-16 pb-12">
        {/* Main Footer Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-12 gap-10 lg:gap-12 pb-14 border-b border-border">
          
          {/* Column 1: Brand & Publisher Mission */}
          <div className="lg:col-span-4 space-y-5">
            <Link to="/" className="inline-block transition-opacity duration-150 hover:opacity-90">
              <WvcLogo className="h-7 w-auto" />
            </Link>

            <p className="text-sm text-muted-foreground leading-relaxed max-w-sm">
              Independent B2B intelligence, peer-reviewed market analyses, and executive publications designed for strategic decision-makers.
            </p>

            <div className="pt-2 flex items-center gap-2 text-xs font-mono text-muted-foreground">
              <ShieldCheck className="size-4 text-accent" />
              <span>Independent editorial oversight &bull; No sponsored bias</span>
            </div>

            <div className="space-y-2 pt-1 text-xs text-muted-foreground font-sans">
              <div className="flex items-start gap-2.5">
                <MapPin className="size-3.5 mt-0.5 shrink-0 text-muted-foreground/80" />
                <span>123 Main Street, Springfield, USA</span>
              </div>
              <div className="flex items-center gap-2.5">
                <Mail className="size-3.5 shrink-0 text-muted-foreground/80" />
                <a href="mailto:info@minfomedia.com" className="transition-colors duration-150 hover:text-foreground">
                  info@minfomedia.com
                </a>
              </div>
              <div className="flex items-center gap-2.5">
                <Phone className="size-3.5 shrink-0 text-muted-foreground/80" />
                <a href="tel:5551234567" className="transition-colors duration-150 hover:text-foreground">
                  (555) 123-4567
                </a>
              </div>
            </div>
          </div>

          {/* Column 2: Research & Publications */}
          <div className="lg:col-span-3 space-y-4">
            <h2 className="text-xs font-semibold tracking-wider uppercase text-foreground">
              Publications
            </h2>
            <ul className="space-y-2.5">
              {publicationSections.map((item, i) => <li key={item.to} data-index={i}>
                  <Link to={item.to} className="text-sm text-muted-foreground transition-colors duration-150 hover:text-foreground inline-flex items-center gap-1 group">
                    <span>{item.label}</span>
                    <ArrowUpRight className="size-3 opacity-0 -translate-x-1 group-hover:opacity-100 group-hover:translate-x-0 transition-[opacity,transform] duration-150" />
                  </Link>
                </li>)}
            </ul>
          </div>

          {/* Column 3: Secondary Navigation from MenuProvider */}
          <div className="lg:col-span-2 space-y-4">
            <h2 className="text-xs font-semibold tracking-wider uppercase text-foreground">
              Navigation
            </h2>
            <MenuProvider menu_id="17" className="footer-menu">
              <FooterMenuConsumer />
            </MenuProvider>
          </div>

          {/* Column 4: Governance & Editorial Standards */}
          <div className="lg:col-span-3 space-y-4">
            <h2 className="text-xs font-semibold tracking-wider uppercase text-foreground">
              Governance
            </h2>
            <ul className="space-y-2.5">
              {editorialGovernance.map((item, i) => <li key={item.to} data-index={i}>
                  <Link to={item.to} className="text-sm text-muted-foreground transition-colors duration-150 hover:text-foreground">
                    {item.label}
                  </Link>
                </li>)}
            </ul>

            <div className="p-3 bg-muted/60 border border-border rounded-sm mt-4">
              <p className="text-[11px] font-mono leading-relaxed text-muted-foreground">
                Distribution: Global digital editions and executive research digests. ISSN standards compliant.
              </p>
            </div>
          </div>

        </div>

        {/* Bottom Bar: Publisher Disclaimer and Legal Links */}
        <div className="pt-8 flex flex-col md:flex-row items-start md:items-center justify-between gap-4 text-xs text-muted-foreground">
          <div className="space-y-1">
            <p>
              &copy; {new Date().getFullYear()} MinfoMedia Publishing Group. All editorial rights reserved.
            </p>
            <p className="text-[11px] text-muted-foreground/80 max-w-2xl">
              Research publications and market indexes are prepared for institutional planning and executive analysis. No part of this publication may be reproduced without prior written permission.
            </p>
          </div>

          <div className="flex items-center gap-4 shrink-0 font-sans">
            <Link to="/PrivacyDataProtectionPage" className="text-xs text-muted-foreground hover:text-foreground transition-colors duration-150 underline-offset-4 hover:underline">
              Privacy &amp; Data Protection
            </Link>
            <Separator orientation="vertical" className="h-3" />
            <Link to="/TermsConditionsPage" className="text-xs text-muted-foreground hover:text-foreground transition-colors duration-150 underline-offset-4 hover:underline">
              Terms &amp; Conditions
            </Link>
          </div>
        </div>
      </div>
    </section>;
}